// Updated toDetailedPlan.kt with special cases for SimpleQuestionSeries and restriction end times
package com.exura.features.legacyplans.domain.mapper

import com.exura.features.legacyplans.domain.model.*
import com.exura.features.plandefinition.domain.model.collection.Collection
import com.exura.features.plandefinition.domain.model.element.FloatingElement
import com.exura.features.plandefinition.domain.model.element.InstructionElement
import com.exura.features.plandefinition.domain.model.element.PersistentElement
import com.exura.features.plandefinition.domain.model.element.QuestionElement
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.domain.model.series.BaseSeries
import com.exura.features.plandefinition.domain.model.series.PartOfDay
import com.exura.features.plandefinition.domain.model.series.SimpleQuestionSeries
import kotlinx.datetime.*

/**
 * Maps a BasePlan (new structure) to a DetailedPlan (legacy structure)
 * for backward compatibility
 */
fun BasePlan.toDetailedPlan(
    startDate: LocalDateTime,
    newPlanId: String? = null,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
    ): DetailedPlan {
    // Generate plan ID
    val detailedPlanId = newPlanId ?: firebaseDocId!!

    // Group series from collections and direct series
    val allSeriesCollections = collectSeriesCollections()

    // Process each series to generate appropriate timely elements with unique notification IDs
    val (tasks, restrictions, checkups) = processAllSeries(allSeriesCollections, detailedPlanId, startDate, dateCalculator = dateCalculator)

    return DetailedPlan(
        id = detailedPlanId,
        title = title,
        shortDescription = description,
        tasks = tasks,
        checkups = checkups, // Filter out null checkups from failed conversions
        restrictions = restrictions,
        status = "pending"
    )
}

/**
 * Collects all series from collections and direct series
 */
private fun BasePlan.collectSeriesCollections(): List<Pair<Collection?, BaseSeries<*>>> {
    val result = mutableListOf<Pair< Collection?, BaseSeries<*>>>()

    // Add series from collections
    collections.forEach { collection ->
        result.addAll(collection.series.map { collection to it })
    }

    // Add direct series
    result.addAll(directSeries.map {null to it})

    return result
}

/**
 * Process all series to generate timely elements with unique notification IDs
 */
private fun processAllSeries(
    allSeries: List<Pair<Collection?, BaseSeries<*>>>,
    planId: String,
    startDate: LocalDateTime,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): Triple<List<TimelyTask>, List<TimelyRestriction>, List<TimelyCheckup>> {
    val tasks = mutableListOf<TimelyTask>()
    val restrictions = mutableListOf<TimelyRestriction>()
    val checkups = mutableListOf<TimelyCheckup?>()

    var notificationIdCounter = 1 // Start from 1 for unique notification IDs

    allSeries.forEach { colSeries ->
        val collection = colSeries.first
        val series = colSeries.second
        val seriesId = series.firebaseDocId ?: "series-${series.title.hashCode()}"
        val instructionId = "1_inst" // Placeholder value used everywhere

        val offset = collection?.relativeDay ?: 0
        // SPECIAL CASE: Handle SimpleQuestionSeries to spawn from series instead of elements
        if (series is SimpleQuestionSeries) {
            checkups.add(
                series.toTimelyCheckupFromSeries(
                    planId, instructionId, seriesId, startDate, notificationIdCounter++,series.description, dateCalculator = dateCalculator, collectionDayOffset = offset,
                )
            )
        } else {
            // Regular processing: spawn elements from the series
            val elements = series.spawnElements()

            elements.forEachIndexed { index, element ->
                val elementId = element.firebaseDocId ?: "$seriesId-$index"

                when (element) {
                    is InstructionElement -> tasks.add(
                        element.toTimelyTask(
                            planId,
                            instructionId,
                            seriesId,
                            elementId,
                            startDate,
                            notificationIdCounter++,
                            series.description,
                            dateCalculator = dateCalculator,
                            collectionDayOffset = offset,
                        )
                    )

                    is FloatingElement -> tasks.add(
                        element.toTimelyTask(
                            planId,
                            instructionId,
                            seriesId,
                            elementId,
                            startDate,
                            notificationIdCounter++,
                            series.description,
                            dateCalculator = dateCalculator,
                            collectionDayOffset = offset,
                        )
                    )

                    is PersistentElement -> restrictions.add(
                        element.toTimelyRestriction(
                            planId,
                            instructionId,
                            seriesId,
                            elementId,
                            startDate,
                            notificationIdCounter++,
                            dateCalculator = dateCalculator,
                            collectionDayOffset = offset,
                        )
                    )

                    is QuestionElement -> checkups.add(
                        element.toTimelyCheckup(
                            planId,
                            instructionId,
                            seriesId,
                            elementId,
                            startDate,
                            notificationIdCounter++,
                            series.description,
                            dateCalculator = dateCalculator,
                            collectionDayOffset = offset,
                        )
                    )
                }
            }
        }
    }

    return Triple(tasks, restrictions, checkups.filterNotNull())
}

/**
 * Converts an InstructionElement to a TimelyTask
 */
private fun InstructionElement.toTimelyTask(
    planId: String,
    instructionId: String,
    seriesId: String,
    elementId: String,
    startDate: LocalDateTime,
    notificationId: Int,
    description: String,
    collectionDayOffset: Int = 0,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): TimelyTask {
    // Calculate element date with proper timezone handling
    val elementDate = dateCalculator.calculateDate(startDate, relativeDay + collectionDayOffset)

    // Adjust time based on partOfDay or exactTime
    val finalDate = adjustTimeOfDay(elementDate)

    // End date is start date plus duration
    val endDate = addMinutes(finalDate, durationInMinutes)

    return TimelyTask(
        id = elementId,
        planId = planId,
        instructionId = instructionId,
        seriesId = seriesId,
        startFrom = finalDate,
        endAt = endDate,
        notificationId = notificationId,
        title = instruction,
        description = description,
        tutorial = "",
        videoUrl = "",
        partOfDay = partOfDay,
        doneAt = null,
    )
}

/**
 * Adjusts the time of day based on partOfDay or exactTime
 */
private fun InstructionElement.adjustTimeOfDay(date: LocalDateTime): LocalDateTime {
    return when {
        exactTime != null -> date.copyHourAndMinute(exactTime.hour, exactTime.minute)
        partOfDay != null -> {
            // Map part of day to approximate hour
            val hour = when (partOfDay) {
                PartOfDay.MORNING -> 8
                PartOfDay.NOON -> 12
                PartOfDay.AFTERNOON -> 15
                PartOfDay.EVENING -> 19
                PartOfDay.NIGHT -> 22
                PartOfDay.TODAY -> 12 // Default to noon for "today"
            }
            date.copyHourAndMinute(hour, 0)
        }

        else -> date
    }
}

/**
 * Converts a FloatingElement to a TimelyTask
 */
private fun FloatingElement.toTimelyTask(
    planId: String,
    instructionId: String,
    seriesId: String,
    elementId: String,
    startDate: LocalDateTime,
    notificationId: Int,
    description: String,
    collectionDayOffset: Int = 0,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): TimelyTask {
    // If deadline specified, set end date accordingly
    val endDate = deadline?.let {
        dateCalculator.calculateDate(startDate, it + collectionDayOffset)
    } ?: dateCalculator.calculateDate(startDate, 7 + collectionDayOffset) // Default 7-day window

    // If exact time specified, adjust timing
    val adjustedEndDate = exactTime?.let {
        endDate.copyHourAndMinute(it.hour, it.minute)
    } ?: endDate

    return TimelyTask(
        id = elementId,
        planId = planId,
        instructionId = instructionId,
        seriesId = seriesId,
        startFrom = startDate,
        endAt = adjustedEndDate,
        notificationId = notificationId,
        title = instruction,
        description = description,
        tutorial = "",
        videoUrl = "",
        partOfDay = null,
        doneAt = null
    )
}

/**
 * Converts a PersistentElement to a TimelyRestriction
 */
private fun PersistentElement.toTimelyRestriction(
    planId: String,
    instructionId: String,
    seriesId: String,
    elementId: String,
    startDate: LocalDateTime,
    notificationId: Int,
    collectionDayOffset: Int = 0,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): TimelyRestriction {
    val startElementDate = dateCalculator.calculateDate(startDate, startRelativeDay + collectionDayOffset)
    val endElementDate = dateCalculator.calculateDate(startDate, endRelativeDay + collectionDayOffset)

    // Adjust times if specified
    val finalStartDate = startRelativeHourOfDay?.let {
        startElementDate.copyHourAndMinute(it.hour, it.minute)
    } ?: startElementDate

    val finalEndDate = endRelativeHourOfDay?.let {
        endElementDate.copyHourAndMinute(it.hour, it.minute)
    } ?: endElementDate.copyHourAndMinute(23, 59)

    return TimelyRestriction(
        id = elementId,
        planId = planId,
        instructionId = instructionId,
        seriesId = seriesId,
        startFrom = finalStartDate,
        endAt = finalEndDate,
        notificationId = notificationId,
        title = title,
        description = description,
        icon = icon ?: ""
    )
}

/**
 * Converts a QuestionElement to a TimelyCheckup
 * Returns null if yes/no options are not found and logs the issue
 */
private fun QuestionElement.toTimelyCheckup(
    planId: String,
    instructionId: String,
    seriesId: String,
    elementId: String,
    startDate: LocalDateTime,
    notificationId: Int,
    description: String,
    collectionDayOffset: Int = 0,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): TimelyCheckup? {
    // Calculate date based on relative day with proper timezone handling
    val questionDate = dateCalculator.calculateDate(startDate, relativeDay + collectionDayOffset)

    // Create answer window (24h by default)
    val answerWindowStart = questionDate
    val answerWindowEnd = dateCalculator.calculateDate(questionDate, 1)

    // Look for yes/no options in the question element
    val yesOption = options.find {
        it.id.lowercase() == "yes" || it.text.lowercase() == "yes"
    }
    val noOption = options.find {
        it.id.lowercase() == "no" || it.text.lowercase() == "no"
    }

    // Check if both yes and no options exist
    if (yesOption == null || noOption == null) {
        println("Warning: Question element '$elementId' (title: '$title') is missing yes/no options. " +
                "Available options: ${options.map { "${it.id}:${it.text}" }}. Skipping this checkup."
        )
        return null
    }

    // Create answers based on the question element options
    val yesAnswer = Answer(
        answer = yesOption.responseMessage ?: "",
        positive = yesOption.isResponsePositive
    )
    val noAnswer = Answer(
        answer = noOption.responseMessage ?: "",
        positive = noOption.isResponsePositive
    )

    return TimelyCheckup(
        id = elementId,
        planId = planId,
        instructionId = instructionId,
        seriesId = seriesId,
        startFrom = questionDate,
        endAt = answerWindowEnd,
        notificationId = notificationId,
        title = question,
        description = description,
        sensitive = false,
        imageUrl = imageUrl ?: "",
        videoUrl = "",
        answerWindow = AnswerWindow(
            startFrom = answerWindowStart,
            endAt = answerWindowEnd
        ),
        yesWithinWindow = yesAnswer,
        yesOutsideWindow = yesAnswer,
        noWithinWindow = noAnswer,
        noOutsideWindow = noAnswer,
        doneAt = null
    )
}

// JS-specific fallback that uses native Date operations
//private fun calculateDateJS(baseDate: LocalDateTime, daysToAdd: Int): LocalDateTime {
//    try {
//        // Convert LocalDateTime to ISO string for JS Date constructor
//        val isoString = "${baseDate.date}T${baseDate.time}"
//
//        // Create JS Date and add days
//        val jsDate = js("")
//        jsDate.setDate(jsDate.getDate() + daysToAdd)
//
//        // Get back ISO string and parse to LocalDateTime
//        val resultIso = (jsDate.toISOString() as String)
//        val datePart = resultIso.substringBefore('T')
//        val timePart = resultIso.substringAfter('T').substringBefore('.')
//
//        return LocalDateTime.parse("${datePart}T$timePart")
//    } catch (e: Throwable) {
//        // Ultimate fallback - return original date
//        console.warn("JS date calculation failed, returning original date", e.message)
//        return baseDate
//    }
//}

//private fun calculateDate(baseDate: LocalDateTime, daysToAdd: Int): LocalDateTime {
//    return baseDate.date.plus(DatePeriod(days = daysToAdd)).atTime(baseDate.time)
//}

/**
 * Adds minutes to a LocalDateTime, handling timezone properly
 */
private fun addMinutes(date: LocalDateTime, minutes: Int): LocalDateTime {
    return date.toInstant(TimeZone.currentSystemDefault())
        .plus(minutes, DateTimeUnit.MINUTE, TimeZone.currentSystemDefault())
        .toLocalDateTime(TimeZone.currentSystemDefault())
}

/**
 * Helper extension function to update hours and minutes in LocalDateTime
 */
private fun LocalDateTime.copyHourAndMinute(hour: Int, minute: Int): LocalDateTime {
    return LocalDateTime(
        year = year,
        monthNumber = monthNumber,
        dayOfMonth = dayOfMonth,
        hour = hour,
        minute = minute,
        second = 0,
        nanosecond = 0
    )
}

private fun SimpleQuestionSeries.toTimelyCheckupFromSeries(
    planId: String,
    instructionId: String,
    seriesId: String,
    startDate: LocalDateTime,
    notificationId: Int,
    description: String,
    collectionDayOffset: Int = 0,
    dateCalculator: DateCalculator = DefaultDateCalculator(),
): TimelyCheckup? {
    // Calculate date based on series relative day
    val questionDate = dateCalculator.calculateDate(startDate, relativeDay + collectionDayOffset)

    // Create broader answer window covering the entire duration of the series
    val answerWindowStart = questionDate
    val answerWindowEnd = dateCalculator.calculateDate(questionDate, durationInDays())

    // Look for yes/no options in the series options
    val yesOption = options.find {
        it.id.lowercase() == "yes" || it.text.lowercase() == "yes"
    }
    val noOption = options.find {
        it.id.lowercase() == "no" || it.text.lowercase() == "no"
    }

    // Check if both yes and no options exist
    if (yesOption == null || noOption == null) {
        println("Warning: SimpleQuestionSeries '$seriesId' (name: '$title') is missing yes/no options. " +
                "Available options: ${options.map { "${it.id}:${it.text}" }}. Skipping this checkup."
        )
        return null
    }

    val yesAnswer = Answer(
        answer = yesOption.responseMessage ?: "",
        positive = yesOption.isResponsePositive,
    )
    val noAnswer = Answer(
        answer = noOption.responseMessage ?: "",
        positive = noOption.isResponsePositive,
    )

    return TimelyCheckup(
        id = seriesId, // Use series ID instead of element ID
        planId = planId,
        instructionId = instructionId,
        seriesId = seriesId,
        startFrom = answerWindowStart,
        endAt = answerWindowEnd,
        notificationId = notificationId,
        title = question,
        description = description,
        sensitive = false,
        imageUrl = imageUrl ?: "",
        videoUrl = "",
        answerWindow = AnswerWindow(
            startFrom = answerWindowStart,
            endAt = answerWindowEnd,
        ),
        yesWithinWindow = yesAnswer,
        yesOutsideWindow = Answer("", false),
        noWithinWindow = noAnswer,
        noOutsideWindow = Answer("", false),
        doneAt = null
    )
}
